import AppointmentsAPI from 'dashboard/api/appointments';

const state = {
  records: [],
  contactAppointments: {}, // { contactId: [appointments] }
  selectedAppointment: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getAppointments: _state => _state.records,
  getSelectedAppointment: _state => _state.selectedAppointment,
  getUIFlags: _state => _state.uiFlags,
  getAppointmentById: _state => id => _state.records.find(a => a.id === Number(id)),
  getByContact: _state => contactId => _state.contactAppointments[contactId] || [],
  getAppointmentsForDate: _state => date => {
    const targetDate = new Date(date).toDateString();
    return _state.records.filter(a => {
      const startDate = new Date(a.start_time).toDateString();
      return startDate === targetDate;
    });
  },
  getAppointmentsForDateRange: _state => (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return _state.records.filter(a => {
      const appointmentDate = new Date(a.start_time);
      return appointmentDate >= startDate && appointmentDate <= endDate;
    });
  },
  getUpcomingAppointments: _state => {
    const now = new Date();
    return _state.records
      .filter(a => new Date(a.start_time) >= now && a.status !== 'cancelled')
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .slice(0, 5);
  },
};

const mutations = {
  SET_APPOINTMENTS(_state, appointments) {
    _state.records = appointments;
  },
  ADD_APPOINTMENT(_state, appointment) {
    const exists = _state.records.find(a => a.id === appointment.id);
    if (!exists) {
      _state.records.push(appointment);
    }
  },
  UPDATE_APPOINTMENT(_state, appointment) {
    const index = _state.records.findIndex(a => a.id === appointment.id);
    if (index !== -1) {
      _state.records.splice(index, 1, appointment);
    }
  },
  DELETE_APPOINTMENT(_state, id) {
    _state.records = _state.records.filter(a => a.id !== id);
  },
  SET_SELECTED_APPOINTMENT(_state, appointment) {
    _state.selectedAppointment = appointment;
  },
  SET_CONTACT_APPOINTMENTS(_state, { contactId, appointments }) {
    _state.contactAppointments = {
      ..._state.contactAppointments,
      [contactId]: appointments,
    };
  },
  SET_UI_FLAG(_state, { key, value }) {
    _state.uiFlags[key] = value;
  },
};

const actions = {
  async fetchAppointments({ commit }, params = {}) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await AppointmentsAPI.get(params);
      commit('SET_APPOINTMENTS', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async fetchByContact({ commit }, contactId) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await AppointmentsAPI.get({ contact_id: contactId });
      commit('SET_CONTACT_APPOINTMENTS', { contactId, appointments: data });
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async fetchAppointment({ commit }, id) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await AppointmentsAPI.show(id);
      commit('SET_SELECTED_APPOINTMENT', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async createAppointment({ commit }, appointmentData) {
    commit('SET_UI_FLAG', { key: 'isCreating', value: true });
    try {
      const { data } = await AppointmentsAPI.create(appointmentData);
      commit('ADD_APPOINTMENT', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isCreating', value: false });
    }
  },

  async updateAppointment({ commit }, { id, ...appointmentData }) {
    commit('SET_UI_FLAG', { key: 'isUpdating', value: true });
    try {
      const { data } = await AppointmentsAPI.update(id, appointmentData);
      commit('UPDATE_APPOINTMENT', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isUpdating', value: false });
    }
  },

  async deleteAppointment({ commit }, id) {
    commit('SET_UI_FLAG', { key: 'isDeleting', value: true });
    try {
      await AppointmentsAPI.delete(id);
      commit('DELETE_APPOINTMENT', id);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isDeleting', value: false });
    }
  },

  async completeAppointment({ commit }, id) {
    try {
      const { data } = await AppointmentsAPI.complete(id);
      commit('UPDATE_APPOINTMENT', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async cancelAppointment({ commit }, id) {
    try {
      const { data } = await AppointmentsAPI.cancel(id);
      commit('UPDATE_APPOINTMENT', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  setSelectedAppointment({ commit }, appointment) {
    commit('SET_SELECTED_APPOINTMENT', appointment);
  },

  clearSelectedAppointment({ commit }) {
    commit('SET_SELECTED_APPOINTMENT', null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
